import { Alert, Button, Flex, Modal, Radio, Select, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { InputNumber } from "../../../../components/Input/InputNumber.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { readSettingsError } from "../settings-error-envelope.ts"
import {
  currentMonthStart,
  monthLabel,
  nextMonthStart,
  selectableLaterMonths
} from "./budget-months.ts"
import { saveLeadSourceBudget } from "./lead-source-budgets.services.ts"
import type {
  BudgetApplyFrom,
  LeadSourceBudgetRow
} from "./lead-source-budgets.types.ts"

const { Text } = Typography

export interface LeadSourceBudgetModalProps {
  budget: LeadSourceBudgetRow
  onClose: () => void
  /** Called after a save, and to fetch the latest when a save was refused as stale. */
  onSaved: () => void | Promise<void>
}

const MAX_BUDGET = 9_999_999_999.99

/** Set a lead source's monthly budget from this month or the next. Changing
 *  the amount requires choosing which; the summary states what will change
 *  before anything is written. */
export const LeadSourceBudgetModal = ({
  budget,
  onClose,
  onSaved
}: LeadSourceBudgetModalProps) => {
  const { t, i18n } = useTranslation()
  const inForce = budget.current ? Number(budget.current.amount) : null
  const [amount, setAmount] = useState<number | null>(inForce)
  const [applyFrom, setApplyFrom] = useState<BudgetApplyFrom | null>(null)
  const [laterMonth, setLaterMonth] = useState<string | null>(null)
  const [reason, setReason] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stale, setStale] = useState(false)

  const month = (value: string) => monthLabel(value, i18n.language)
  const money = (value: number | string) => budgetFormat(value)
  const thisMonth = currentMonthStart()
  const nextMonth = nextMonthStart()

  const changed = amount !== null && amount !== inForce
  const laterMonths = selectableLaterMonths()
  const firstMonth =
    applyFrom === "specific_month"
      ? laterMonth
      : applyFrom === "next_month"
        ? nextMonth
        : thisMonth
  const replacesScheduled =
    applyFrom !== "current_month" &&
    !!firstMonth &&
    budget.scheduled?.effective_month === firstMonth
  const canSave =
    changed &&
    applyFrom !== null &&
    (applyFrom !== "specific_month" || !!laterMonth) &&
    !saving &&
    !stale

  const save = async () => {
    if (!canSave || amount === null || applyFrom === null) return
    setSaving(true)
    setError(null)
    const response = await saveLeadSourceBudget({
      source: budget.source,
      amount: amount.toFixed(2),
      apply_from: applyFrom,
      reason: reason.trim(),
      expected_version: budget.version,
      ...(applyFrom === "specific_month" && laterMonth
        ? { month: laterMonth }
        : {})
    })
    setSaving(false)
    if (response.ok) {
      await onSaved()
      onClose()
      return
    }
    if (response.status === 409) {
      setStale(true)
      return
    }
    setError(
      readSettingsError(response.data) || t("leadSourceBudgets.errors.save")
    )
  }

  const reload = async () => {
    await onSaved()
    onClose()
  }

  return (
    <Modal
      open
      title={t("leadSourceBudgets.modal.title", { source: budget.label })}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key={"cancel"} onClick={onClose}>
          {t("leadSourceBudgets.modal.cancel")}
        </Button>,
        <Button
          key={"save"}
          type={"primary"}
          loading={saving}
          disabled={!canSave}
          onClick={() => void save()}
        >
          {t("leadSourceBudgets.modal.save")}
        </Button>
      ]}
    >
      <Flex
        vertical
        gap={"middle"}
        onKeyDown={(event) => {
          // Enter inside the month picker chooses a month; it must not also
          // save with the month that was picked before.
          const inSelect = (event.target as HTMLElement).closest?.(
            ".ant-select"
          )
          if (event.key === "Enter" && !event.shiftKey && !inSelect) {
            event.preventDefault()
            void save()
          }
        }}
      >
        <Flex vertical gap={2}>
          <Text type={"secondary"}>
            {budget.current
              ? t("leadSourceBudgets.modal.inForce", {
                  amount: money(budget.current.amount),
                  month: month(budget.current.effective_month)
                })
              : t("leadSourceBudgets.modal.notSet")}
          </Text>
          {budget.scheduled && (
            <Text type={"secondary"}>
              {t("leadSourceBudgets.modal.scheduled", {
                amount: money(budget.scheduled.amount),
                month: month(budget.scheduled.effective_month)
              })}
            </Text>
          )}
        </Flex>

        <Flex vertical gap={4}>
          <label htmlFor={"lead-source-budget-amount"}>
            <Text strong>{t("leadSourceBudgets.modal.amount")}</Text>
          </label>
          <InputNumber
            id={"lead-source-budget-amount"}
            autoFocus
            min={0}
            max={MAX_BUDGET}
            precision={2}
            prefix={"$"}
            style={{ width: "100%" }}
            value={amount}
            onChange={(value) =>
              setAmount(value === null ? null : Number(value))
            }
          />
        </Flex>

        {changed && (
          <Flex vertical gap={4}>
            <Text strong id={"lead-source-budget-apply"}>
              {t("leadSourceBudgets.modal.applyQuestion")}
            </Text>
            <Radio.Group
              aria-labelledby={"lead-source-budget-apply"}
              value={applyFrom}
              onChange={(event) => setApplyFrom(event.target.value)}
            >
              <Flex vertical gap={4}>
                <Radio value={"current_month"}>
                  {t("leadSourceBudgets.modal.currentMonth", {
                    month: month(thisMonth)
                  })}
                </Radio>
                <Radio value={"next_month"}>
                  {t("leadSourceBudgets.modal.nextMonth", {
                    month: month(nextMonth)
                  })}
                </Radio>
                <Radio value={"specific_month"}>
                  {t("leadSourceBudgets.modal.laterMonth")}
                </Radio>
                {applyFrom === "specific_month" && (
                  <Select
                    aria-label={t("leadSourceBudgets.modal.laterMonthPick")}
                    virtual={false}
                    placeholder={t("leadSourceBudgets.modal.laterMonthPick")}
                    style={{ marginInlineStart: 24, maxWidth: 220 }}
                    value={laterMonth}
                    onChange={setLaterMonth}
                    options={laterMonths.map((value) => ({
                      value,
                      label: month(value)
                    }))}
                  />
                )}
              </Flex>
            </Radio.Group>
          </Flex>
        )}

        <Flex vertical gap={4}>
          <label htmlFor={"lead-source-budget-reason"}>
            <Text strong>{t("leadSourceBudgets.modal.reason")}</Text>
          </label>
          <TextInput
            id={"lead-source-budget-reason"}
            maxLength={500}
            placeholder={t("leadSourceBudgets.modal.reasonPlaceholder")}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Flex>

        {changed && applyFrom && firstMonth && amount !== null && (
          <Alert
            type={"info"}
            showIcon
            message={t("leadSourceBudgets.modal.summary", {
              source: budget.label,
              from:
                inForce === null
                  ? t("leadSourceBudgets.notSet")
                  : money(inForce),
              to: money(amount),
              month: firstMonth ? month(firstMonth) : ""
            })}
            description={
              replacesScheduled && budget.scheduled
                ? t("leadSourceBudgets.modal.replacesScheduled", {
                    amount: money(budget.scheduled.amount),
                    month: month(budget.scheduled.effective_month)
                  })
                : applyFrom === "current_month" && budget.scheduled
                  ? t("leadSourceBudgets.modal.keepsScheduled", {
                      amount: money(budget.scheduled.amount),
                      month: month(budget.scheduled.effective_month)
                    })
                  : undefined
            }
          />
        )}

        {stale && (
          <Alert
            type={"warning"}
            showIcon
            message={t("leadSourceBudgets.errors.stale")}
            action={
              <Button size={"small"} onClick={() => void reload()}>
                {t("leadSourceBudgets.actions.reload")}
              </Button>
            }
          />
        )}
        {error && <Alert type={"error"} showIcon message={error} />}
      </Flex>
    </Modal>
  )
}
